import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ChartCard from "./ChartCard";

export default function ChiffreAffaires() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [viewMode, setViewMode] = useState('table'); // 'table' ou 'graph'

    useEffect(() => {
        fetchAnnees();
        fetchData();
    }, [periode, annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stats.php?action=annees',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/stats.php?action=ca&periode=${periode}&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

const totalCA = data.reduce((sum, item) => {
    // Convertir en nombre en s'assurant que c'est bien un nombre
    const valeur = parseFloat(item.ca) || 0;
    return sum + valeur;
}, 0);
    const totalFactures = data.reduce((sum, item) => sum + (item.nb_factures || 0), 0);
    const totalClients = data.reduce((sum, item) => sum + (item.nb_clients || 0), 0);
    const panierMoyen = totalFactures > 0 ? totalCA / totalFactures : 0;

    const getPeriodeLabel = (item) => {
        if (periode === 'mois') {
            return new Date(annee, item.mois - 1, 1).toLocaleString('fr-FR', { month: 'long' });
        } else if (periode === 'trimestre') {
            return `T${item.trimestre} ${item.annee}`;
        } else {
            return `${item.annee}`;
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Analyse du chiffre d'affaires...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchData}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-bar-chart-2-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Chiffre d'affaires
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Analyse détaillée par période • {annee}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'graph' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('graph')}
                                        className="rounded-pill px-3 me-2"
                                    >
                                        <i className="ri-bar-chart-2-line me-1"></i>Graphique
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')} className="rounded-pill px-4">
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-line me-2"></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => setPeriode(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            <option value="mois">📊 Mensuel</option>
                                            <option value="trimestre">📈 Trimestriel</option>
                                            <option value="annee">📅 Annuel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-2-line me-2"></i>
                                            Année
                                        </Form.Label>
                                        <Form.Select 
                                            value={annee} 
                                            onChange={(e) => setAnnee(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            {annees.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={fetchData}
                                            className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                border: 'none',
                                                borderRadius: '10px'
                                            }}
                                        >
                                            <i className="ri-refresh-line"></i>
                                            <span>Actualiser</span>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Cartes de résumé */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col lg={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                                            <i className="ri-money-euro-circle-line fs-3"></i>
                                        </div>
                                        <div>
                                            <small className="text-white-50">CA Total</small>
                                            <h4 className="mb-0 fw-bold">{formatCurrency(totalCA)}</h4>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                                            <i className="ri-file-list-line fs-3"></i>
                                        </div>
                                        <div>
                                            <small className="text-white-50">Factures</small>
                                            <h4 className="mb-0 fw-bold">{formatNumber(totalFactures)}</h4>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                                            <i className="ri-group-line fs-3"></i>
                                        </div>
                                        <div>
                                            <small className="text-white-50">Clients</small>
                                            <h4 className="mb-0 fw-bold">{formatNumber(totalClients)}</h4>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                color: 'white'
                            }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                                            <i className="ri-shopping-basket-line fs-3"></i>
                                        </div>
                                        <div>
                                            <small className="text-white-50">Panier moyen</small>
                                            <h4 className="mb-0 fw-bold">{formatCurrency(panierMoyen)}</h4>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm table-card">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="ri-file-list-line me-2 text-primary"></i>
                                    Détail par {periode === 'mois' ? 'mois' : periode === 'trimestre' ? 'trimestre' : 'année'}
                                </h5>
                                <Badge bg="primary" className="px-3 py-2">
                                    {data.length} période(s)
                                </Badge>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                ) : data.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                        <h5>Aucune donnée disponible</h5>
                                        <p className="text-muted">Aucune facture trouvée pour cette période</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover className="mb-0 align-middle custom-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Période</th>
                                                    <th className="text-end">Chiffre d'affaires</th>
                                                    <th className="text-end">Factures</th>
                                                    <th className="text-end">Clients</th>
                                                    <th className="text-end">Panier moyen</th>
                                                    <th className="text-center">Tendance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.map((item, index) => {
                                                    const caPrecedent = index > 0 ? data[index - 1].ca : item.ca;
                                                    const evolution = caPrecedent > 0 ? ((item.ca - caPrecedent) / caPrecedent * 100).toFixed(1) : 0;
                                                    const libelle = getPeriodeLabel(item);
                                                    
                                                    return (
                                                        <tr key={index} style={{
                                                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                        }}>
                                                            <td className="fw-semibold">
                                                                <span className="text-capitalize">{libelle}</span>
                                                            </td>
                                                            <td className="text-end fw-bold text-primary">
                                                                {formatCurrency(item.ca)}
                                                            </td>
                                                            <td className="text-end">
                                                                <Badge bg="info" className="px-3 py-2 rounded-pill">
                                                                    {item.nb_factures}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                {item.nb_clients || <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="text-end fw-semibold text-success">
                                                                {formatCurrency(item.ca / (item.nb_factures || 1))}
                                                            </td>
                                                            <td className="text-center">
                                                                {index > 0 && (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Évolution: {evolution}%</Tooltip>}
                                                                        >
                                                                        <Badge 
                                                                            bg={evolution >= 0 ? 'success' : 'danger'}
                                                                            className="rounded-pill px-3 py-2"
                                                                        >
                                                                            <i className={`ri-arrow-${evolution >= 0 ? 'up' : 'down'}-line me-1`}></i>
                                                                            {Math.abs(evolution)}%
                                                                        </Badge>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Graphique (simulée) */}
                    {viewMode === 'graph' && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white py-3">
                                <h5 className="mb-0">
                                    <i className="ri-bar-chart-2-line me-2 text-primary"></i>
                                    Évolution du chiffre d'affaires
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {data.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-bar-chart-line fs-1 text-muted mb-3"></i>
                                        <h5>Aucune donnée à afficher</h5>
                                    </div>
                                ) : (
                                    <div className="position-relative py-4">
                                        {/* Graphique simulé avec barres */}
                                        <div className="d-flex align-items-end justify-content-around" style={{ minHeight: '250px' }}>
                                            {data.map((item, index) => {
                                                const maxCa = Math.max(...data.map(d => d.ca));
                                                const height = (item.ca / maxCa) * 200;
                                                const libelle = getPeriodeLabel(item);
                                                
                                                return (
                                                    <div key={index} className="text-center" style={{ width: '60px' }}>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{formatCurrency(item.ca)}</Tooltip>}
                                                        >
                                                            <div 
                                                                className="bg-primary rounded-top mb-2 mx-auto"
                                                                style={{ 
                                                                    height: `${height}px`, 
                                                                    width: '40px',
                                                                    transition: 'height 0.3s ease',
                                                                    cursor: 'pointer'
                                                                }}
                                                            ></div>
                                                        </OverlayTrigger>
                                                        <small className="text-muted text-capitalize">{libelle}</small>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* Légende */}
                                        <div className="mt-4 pt-3 border-top text-center text-muted small">
                                            <i className="ri-information-line me-1"></i>
                                            Survolez les barres pour voir les montants
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Pied de tableau avec totaux */}
                    {data.length > 0 && viewMode === 'table' && (
                        <Card className="border-0 shadow-sm mt-3 bg-light">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <span className="text-muted">Total général</span>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex justify-content-end gap-4">
                                            <span className="fw-bold text-primary">{formatCurrency(totalCA)}</span>
                                            <span className="fw-bold text-info">{formatNumber(totalFactures)}</span>
                                            <span className="fw-bold text-success">{formatCurrency(panierMoyen)}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .filter-select {
                        border-radius: 10px;
                        border: 1px solid #e2e8f0;
                        padding: 0.5rem;
                        transition: all 0.2s ease;
                    }

                    .filter-select:focus {
                        border-color: #4361ee;
                        box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}